import React, { useMemo, useState } from 'react'
import { deviceNames } from '../../utils/deviceDefinitions.js'

const FaultsTable = React.memo(({ faults = [], onViewRelatedData }) => {
  const [filter, setFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortField, setSortField] = useState('time')
  const [sortDirection, setSortDirection] = useState('desc')

  const startingFaults = useMemo(() => {
    if (!faults.length) return []
    const sorted = [...faults].sort((a, b) =>
      a.device !== b.device ? a.device.localeCompare(b.device)
      : a.measurement !== b.measurement ? a.measurement.localeCompare(b.measurement)
      : new Date(a.time) - new Date(b.time),
    )
    return sorted.map(f => {
      let desc = ''
      if (f._value === 0 || f._value === '0') desc = 'too low'
      else if (f._value === 1 || f._value === '1') desc = 'too high'
      else if (f._value != null && f._value !== '') desc = String(f._value)
      if (!desc && f.fault != null && f.fault !== '') desc = String(f.fault)
      return {
        time: new Date(f.time).toLocaleString(),
        device: f.device,
        measurement: f.measurement,
        description: desc,
        measurementValue: f.measurementValue ?? '',
        _value: f._value,
        fault: f.fault,
      }
    })
  }, [faults])

  const filteredFaults = useMemo(() => {
    if (!filter) return startingFaults
    const terms = filter.toLowerCase().split(/\s+/).filter(Boolean)
    return startingFaults.filter(f =>
      terms.every(term =>
        (f.device && f.device.toLowerCase().includes(term)) ||
        (f.measurement && f.measurement.toLowerCase().includes(term)) ||
        (f.description && String(f.description).toLowerCase().includes(term)),
      ),
    )
  }, [startingFaults, filter])

  const sortedFaults = useMemo(() => {
    const arr = [...filteredFaults]
    arr.sort((a, b) => {
      let cmp = 0
      if (sortField === 'time') {
        cmp = new Date(a.time) - new Date(b.time)
      } else {
        cmp = String(a[sortField] || '').localeCompare(String(b[sortField] || ''))
      }
      return sortDirection === 'asc' ? cmp : -cmp
    })
    return arr
  }, [filteredFaults, sortField, sortDirection])

  const totalPages = Math.ceil(sortedFaults.length / pageSize) || 1
  const paginatedFaults = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return sortedFaults.slice(start, start + pageSize)
  }, [sortedFaults, currentPage, pageSize])

  React.useEffect(() => { setCurrentPage(1) }, [filter, pageSize])

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleExport = () => {
    const csvHeader = 'StartTime,Device,Fault Type,Description,Value'
    const csvRows = startingFaults.map(f => 
      `"${f.time}",${f.device},${f.measurement},${f.description || ''},${f.measurementValue ?? ''}`,
    )
    const csv = [
      csvHeader,
      ...csvRows,
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `fault-starts-export-${new Date().toISOString().slice(0,10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!startingFaults.length) return null

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0 fw-bold">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Faults
        </h5>
        <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>
          <i className="bi bi-download me-1"></i>Download All Faults
        </button>
      </div>
      <div
        className="d-flex align-items-center flex-wrap mb-3 faults-table-summary-bar"
      >
        <div className="d-flex align-items-center flex-wrap faults-table-group-container">
          <span className="text-muted fw-bold faults-table-group-label">Devices:</span>
          {Array.from(new Set(startingFaults.map(f => f.device))).map((device) => (
            <span
              key={device}
              className={`badge ${getDeviceBadgeClass(device)} px-3 py-2 fw-bold faults-table-group-badge`}
              title={device}
              onClick={() => setFilter(device)}
            >
              {deviceNames[device] || device}
            </span>
          ))}
        </div>
        <div className="faults-table-divider"></div>
        <div className="d-flex align-items-center flex-wrap faults-table-group-container">
          <span className="text-muted fw-bold faults-table-group-label">Fault Types:</span>
          {Array.from(new Set(startingFaults.map(f => f.measurement))).map((type) => (
            <span
              key={type}
              className={`badge ${getFaultBadgeClass(type)} px-3 py-2 fw-bold faults-table-group-badge`}
              title={type}
              onClick={() => setFilter(type)}
            >
              {type}
            </span>
          ))}
        </div>
      </div>
      <div className="mb-2">
        <small className="text-muted">Click a column header to sort. Click again to reverse order.</small>
      </div>
      <div className="row mb-2">
        <div className="col-md-6 faults-table-filter-input-container">
          <input
            className={`form-control form-control-sm ${filter ? 'input-filter-padding' : ''}`}
            type="text"
            placeholder="Filter by device, type, or description..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          {filter && (
            <button
              type="button"
              className="btn btn-sm btn-link faults-table-clear-filter-button"
              aria-label="Clear filter"
              tabIndex={0}
              onClick={() => setFilter('')}
            >
              <i className="bi bi-x faults-table-clear-filter-icon"></i>
            </button>
          )}
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover align-middle">
          <thead>
            <tr>
              <th className="faults-table-header-sortable faults-table-header-time" onClick={() => handleSort('time')} aria-sort={sortField === 'time' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Time
                <span className="faults-table-sort-indicator">
                  {sortField === 'time' ? (sortDirection === 'asc' ? '▲' : '▼') : <span className="faults-table-sort-indicator-hidden">▲</span>}
                </span>
              </th>
              <th className="faults-table-header-sortable faults-table-header-device" onClick={() => handleSort('device')} aria-sort={sortField === 'device' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Device
                <span className="faults-table-sort-indicator">
                  {sortField === 'device' ? (sortDirection === 'asc' ? '▲' : '▼') : <span className="faults-table-sort-indicator-hidden">▲</span>}
                </span>
              </th>
              <th className="faults-table-header-sortable faults-table-header-fault-type" onClick={() => handleSort('measurement')} aria-sort={sortField === 'measurement' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Fault Type
                <span className="faults-table-sort-indicator">
                  {sortField === 'measurement' ? (sortDirection === 'asc' ? '▲' : '▼') : <span className="faults-table-sort-indicator-hidden">▲</span>}
                </span>
              </th>
              <th className="faults-table-header-sortable faults-table-header-description" onClick={() => handleSort('description')} aria-sort={sortField === 'description' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                Description
                <span className="faults-table-sort-indicator">
                  {sortField === 'description' ? (sortDirection === 'asc' ? '▲' : '▼') : <span className="faults-table-sort-indicator-hidden">▲</span>}
                </span>
              </th>
              <th className="faults-table-header-value">Value</th>
              <th className="faults-table-header-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedFaults.map((f) => (
              <tr key={`${f.time}-${f.device}-${f.measurement}`}>
                <td className="faults-table-cell-time">{f.time.replace(/:\d{2}(?=\s*[AP]M)/, '')}</td>
                <td>
                  <span className={`badge ${getDeviceBadgeClass(f.device)}`}>{deviceNames[f.device] || f.device}</span>
                </td>
                <td>
                  <span className={`badge ${getFaultBadgeClass(f.measurement)}`}>{f.measurement}</span>
                </td>
                <td>
                  <span className={`badge ${getDescriptionBadgeClass(f.description)}`}>{f.description}</span>
                </td>
                <td className="faults-table-cell-value">
                  {f.measurementValue ?? ''}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                      const args = {
                        device: f.device,
                        measurement: f.measurement,
                        time: f.time,
                        minutesBefore: 2,
                        minutesAfter: 2,
                      }
                      onViewRelatedData && onViewRelatedData(args)
                    }}
                  >
                    <i className="bi bi-graph-up me-1"></i>View Related Data
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <nav className="d-flex justify-content-between align-items-center mt-2" aria-label="Faults table navigation">
        <div>
          <label htmlFor="faults-page-size" className="visually-hidden">Faults per page</label>
          <select
            id="faults-page-size"
            className="form-select form-select-sm d-inline w-auto me-2"
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map(size => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
          <span className="text-muted">Page {currentPage} of {totalPages}</span>
        </div>
        <div>
          <button
            className="btn btn-sm btn-outline-secondary me-1"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page of faults"
          >
            Prev
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label="Next page of faults"
          >
            Next
          </button>
        </div>
      </nav>
    </div>
  )
})

FaultsTable.displayName = 'FaultsTable'

function getFaultBadgeClass(measurementType) {
  switch (measurementType) {
    case 'volts': return 'badge-volts'
    case 'temp': return 'badge-temp'
    case 'amps': return 'badge-amps'
    default: return 'badge-secondary-custom'
  }
}

function getDeviceBadgeClass(device) {
  if (!device) return 'badge-secondary-custom'
  if (device.startsWith('alt')) return 'badge-device-alt'
  if (device.startsWith('bat')) return 'badge-device-bat'
  if (device.startsWith('regulator')) return 'badge-device-regulator'
  return 'badge-secondary-custom'
}

function getDescriptionBadgeClass(description) {
  if (!description) return 'badge-secondary-custom'
  if (description === 'too high') return 'badge-desc-too-high'
  if (description === 'too low') return 'badge-desc-too-low'
  if (!isNaN(Number(description))) return 'badge-desc-numeric'
  return 'badge-secondary-custom'
}

export default FaultsTable